import React, { useContext, useEffect, useReducer } from 'react';
import styles from './AddressInputStep.module.css';
import { Alert, Button, TextField, InputAdornment, Typography, Stack } from "@mui/material";
import AddressInput from "../AddressInput/AddressInput.tsx";
import { MapsLoadedContext } from "../../types/map.ts";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { setCartAddress } from "../../lib/Checkout/cart.ts";
import { Form } from "@base-ui-components/react";
import { useText } from "../../main.tsx";
import { trackFunnelStart } from "../../utils/funnelTracking.ts";
import { useProject } from "../../context/ProjectContext.tsx";
import type { TextFieldProps } from "@mui/material/TextField";
import {
    addressInputStepReducer,
    initialAddressInputStepState,
} from "./AddressInputStep.reducer";
import { useFunnelStep } from "../../context/FunnelDataContext";
import { FUNNEL_STEP_IDS } from "../../constants/funnelSteps";
import { createLogger } from "../../lib/logger.ts";

const logger = createLogger("AddressInputStep");

export type AddressInputStepData = {
    currentAddress: AddressWithPrediction;
};

const fromGooglePlacePostalAddress = (postalAddress: google.maps.places.PostalAddress): PostalAddress => {
    return {
        regionCode: postalAddress.regionCode,
        languageCode: postalAddress.languageCode,
        postalCode: postalAddress.postalCode,
        sortingCode: postalAddress.sortingCode,
        administrativeArea: postalAddress.administrativeArea,
        locality: postalAddress.locality,
        sublocality: postalAddress.sublocality,
        addressLines: postalAddress.addressLines,
        recipients: postalAddress.recipients,
        organization: postalAddress.organization,
    } as PostalAddress;
}

const AddressInputStep: React.FC = () => {
    const { data: stepData, update: onUpdate, submit: onSubmit } =
        useFunnelStep<AddressInputStepData>(FUNNEL_STEP_IDS.ADDRESS_INPUT);
    const [state, dispatch] = useReducer(
        addressInputStepReducer,
        stepData,
        initialAddressInputStepState
    );
    const { isLoaded } = useContext(MapsLoadedContext);
    const { t } = useText();
    const { loadProjectByAddress } = useProject();

    useEffect(() => {
        dispatch({ type: "HYDRATE", payload: stepData });
    }, [stepData]);

    const handleAddressChange = (address: AddressWithPrediction) => {
        dispatch({ type: "SET_ADDRESS", payload: address });
        onUpdate({ currentAddress: address });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!state.currentAddress.prediction) {
            return;
        }

        try {
            dispatch({ type: "SET_LOADING", payload: true });

            const place = new google.maps.places.Place({ id: state.currentAddress.prediction.placeId });
            const nextAddress: AddressWithPrediction = { ...state.currentAddress };

            // Skip fetching location if already available
            if (!nextAddress.location) {
                await place?.fetchFields({ fields: ["location"] });

                if (place?.location) {
                    nextAddress.location = { lat: place.location.lat(), lng: place.location.lng() };
                }
            }

            if (!nextAddress.postalAddress) {
                await place?.fetchFields({ fields: ["postalAddress"] });

                const postalAddress = place?.postalAddress ? fromGooglePlacePostalAddress(place.postalAddress) : undefined;
                if (postalAddress) {
                    nextAddress.postalAddress = postalAddress;
                    setCartAddress(nextAddress.postalAddress).catch(err => logger.error("Error setting cart address", { error: err }));
                }
            }

            // Load or create project via ProjectContext
            await loadProjectByAddress(nextAddress);

            dispatch({ type: "SET_ADDRESS", payload: nextAddress });
            onUpdate({ currentAddress: nextAddress });
            trackFunnelStart(nextAddress.prediction ? "predicted" : "manual");
            onSubmit({ currentAddress: nextAddress });
            dispatch({ type: "SET_LOADING", payload: false });
        } catch (err) {
            logger.error("Error in handleSubmit", { error: err });
            dispatch({
                type: "SET_ERROR",
                payload: err instanceof Error ? err.message : "Ein Fehler ist aufgetreten.",
            });
            // Error is already set in ProjectContext, displayed via projectError
        }
    };

    return (
        isLoaded &&
        <Form onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ p: 2 }}>
                <Typography variant={"h1"} sx={{ fontSize: "2.5rem" }}>
                    {t('funnel.addressInputStep.title')}
                </Typography>
                <Typography variant={"subtitle2"} sx={{ fontSize: "1.25rem" }}>
                    {t('funnel.addressInputStep.description')}
                </Typography>
            </Stack>
            <Stack direction={{ md: "row", xs: "column" }} spacing={2} sx={{ py: 2 }}>
                <AddressInput
                    id="location-autocomplete"
                    onChange={handleAddressChange}
                    currentAddress={state.currentAddress}
                    renderInput={(params: TextFieldProps) => {
                        return (
                            <TextField
                                {...params}
                                className={styles.addressInput}
                                value={state.currentAddress.address}
                                fullWidth
                                type="text"
                                ref={React.createRef<HTMLInputElement>()}
                                variant="outlined"
                                placeholder="Adresse eingeben"
                                // onChange={handleAddressChange}
                                sx={{ background: 'white', borderRadius: '0.5rem' }}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocationOnOutlinedIcon/>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        );
                    }}
                />
                <Button
                    variant={"contained"}
                    type={"submit"}
                    disabled={!state.currentAddress.prediction}
                    loading={state.isLoading}
                    sx={{
                        borderRadius: '0.5rem',
                        px: "2.5rem",
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        whiteSpace: 'nowrap',
                        height: { xs: '50px', md: 'auto' }
                    }}
                >Jetzt berechnen</Button>
            </Stack>
            {state.error && <Alert severity={state.errorSeverity ?? "error"} sx={{ mt: 2 }}>{state.error}</Alert>}
        </Form>
    );
};

export default AddressInputStep;
